import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { useWeb3 } from './Web3Context.jsx';
import { VOTING_CONTRACT_ADDRESS, VOTING_CONTRACT_ABI } from './contract.js';

const AdminDashboard = () => {
    const { signer, provider } = useWeb3();
    const [activeTab, setActiveTab] = useState('candidate');
    const [candidateName, setCandidateName] = useState('');
    const [candidatePhotoUrl, setCandidatePhotoUrl] = useState('');
    const [whitelistAddresses, setWhitelistAddresses] = useState('');
    const [isPaused, setIsPaused] = useState(false);
    const [startTime, setStartTime] = useState(0);
    const [duration, setDuration] = useState(7);
    const [newOwnerAddress, setNewOwnerAddress] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchStatus = async () => {
            if (provider) {
                try {
                    const contract = new ethers.Contract(VOTING_CONTRACT_ADDRESS, VOTING_CONTRACT_ABI, provider);
                    const [paused, start] = await Promise.all([
                        contract.isVotingPaused(),
                        contract.startTime()
                    ]);
                    setIsPaused(paused);
                    setStartTime(Number(start));
                } catch (e) {
                    console.error("상태 로드 에러:", e);
                }
            }
        };
        fetchStatus();
    }, [provider]);

    const handleStartVoting = async () => {
        if (duration < 1 || duration > 14) {
            alert("투표 기간은 1일에서 14일 사이여야 합니다.");
            return;
        }
        setIsLoading(true);
        try {
            const contractWithSigner = new ethers.Contract(VOTING_CONTRACT_ADDRESS, VOTING_CONTRACT_ABI, signer);
            const tx = await contractWithSigner.startVoting(duration);
            await tx.wait();
            alert("투표가 시작되었습니다!");
            setStartTime(Math.floor(Date.now() / 1000)); // 대략적 로컬 업데이트
        } catch (error) {
            console.error("투표 시작 에러:", error);
            alert("투표 시작 실패: " + (error.reason || error.message));
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddCandidate = async () => {
        if (!candidateName.trim() || !candidatePhotoUrl.trim()) {
            alert("이름과 사진 URL을 모두 입력해주세요.");
            return;
        }
        setIsLoading(true);
        try {
            const contractWithSigner = new ethers.Contract(VOTING_CONTRACT_ADDRESS, VOTING_CONTRACT_ABI, signer);
            const tx = await contractWithSigner.addCandidate(candidateName, candidatePhotoUrl);
            await tx.wait();
            alert("후보자 등록 완료!");
            setCandidateName('');
            setCandidatePhotoUrl('');
        } catch (error) {
            console.error("후보 등록 에러:", error);
            alert("후보자 등록 실패: " + (error.reason || error.message));
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddWhitelist = async () => {
        const addresses = whitelistAddresses.split(',').map(addr => addr.trim()).filter(addr => addr);
        if (addresses.length === 0) {
            alert("지갑 주소를 입력해주세요.");
            return;
        }
        for (const addr of addresses) {
            if (!ethers.isAddress(addr)) {
                alert(`유효하지 않은 이더리움 주소입니다: ${addr}`);
                return;
            }
        }
        setIsLoading(true);
        try {
            const contractWithSigner = new ethers.Contract(VOTING_CONTRACT_ADDRESS, VOTING_CONTRACT_ABI, signer);
            
            // 방안 A: 배열을 최대 200개 단위로 청킹(Chunking)하여 다중 트랜잭션 안전 전송
            const chunkSize = 200;
            for (let i = 0; i < addresses.length; i += chunkSize) {
                const chunk = addresses.slice(i, i + chunkSize);
                const tx = await contractWithSigner.addVotersToWhitelist(chunk);
                await tx.wait();
            }
            alert("화이트리스트 추가 완료!");
            setWhitelistAddresses('');
        } catch (error) {
            console.error("화이트리스트 추가 에러:", error);
            alert("화이트리스트 추가 실패: " + (error.reason || error.message));
        } finally {
            setIsLoading(false);
        }
    };

    const handlePause = async (pause) => {
        setIsLoading(true);
        try {
            const contractWithSigner = new ethers.Contract(VOTING_CONTRACT_ADDRESS, VOTING_CONTRACT_ABI, signer);
            const tx = await contractWithSigner.setManualPause(pause);
            await tx.wait();
            alert(pause ? "투표 일시 정지 완료!" : "투표 재개 완료!");
            setIsPaused(pause);
        } catch (error) {
            console.error("제어 에러:", error);
            alert("투표 제어 실패: " + (error.reason || error.message));
        } finally {
            setIsLoading(false);
        }
    };

    const handleForceEnd = async () => {
        if (!window.confirm("정말로 투표를 즉시 종료하시겠습니까? 이 작업은 되돌릴 수 없습니다.")) return;
        setIsLoading(true);
        try {
            const contractWithSigner = new ethers.Contract(VOTING_CONTRACT_ADDRESS, VOTING_CONTRACT_ABI, signer);
            const tx = await contractWithSigner.forceEndVoting();
            await tx.wait();
            alert("투표가 강제 종료되었습니다.");
        } catch (error) {
            console.error("강제 종료 에러:", error);
            alert("투표 강제 종료 실패: " + (error.reason || error.message));
        } finally {
            setIsLoading(false);
        }
    };

    const handleTransferOwnership = async () => {
        if (!ethers.isAddress(newOwnerAddress)) {
            alert("유효하지 않은 이더리움 주소입니다.");
            return;
        }
        if (!window.confirm("정말로 관리자 권한을 양도하시겠습니까? 이 작업은 되돌릴 수 없으며 더 이상 관리자 페이지에 접근할 수 없습니다.")) return;
        setIsLoading(true);
        try {
            const contractWithSigner = new ethers.Contract(VOTING_CONTRACT_ADDRESS, VOTING_CONTRACT_ABI, signer);
            const tx = await contractWithSigner.transferOwnership(newOwnerAddress);
            await tx.wait();
            alert("관리자 권한 양도 완료!");
            setNewOwnerAddress('');
            // 권한을 잃었으므로 페이지 새로고침을 통해 일반 유저 화면으로 라우팅 유도
            window.location.reload();
        } catch (error) {
            console.error("권한 양도 에러:", error);
            alert("권한 양도 실패: " + (error.reason || error.message));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ marginTop: '20px', padding: '20px', border: '2px solid #ffcccc', borderRadius: '8px', textAlign: 'center' }}>
            <h2 style={{ color: 'red' }}>관리자 대시보드</h2>
            <div style={{ marginBottom: '20px' }}>
                <button onClick={() => setActiveTab('candidate')} disabled={isLoading} style={{ margin: '5px', padding: '10px' }}>후보자 관리</button>
                <button onClick={() => setActiveTab('whitelist')} disabled={isLoading} style={{ margin: '5px', padding: '10px' }}>유권자 관리</button>
                <button onClick={() => setActiveTab('control')} disabled={isLoading} style={{ margin: '5px', padding: '10px' }}>투표 제어</button>
            </div>

            {activeTab === 'candidate' && (
                <div>
                    <h3>후보자 등록</h3>
                    <input type="text" placeholder="후보자 이름" value={candidateName} onChange={(e) => setCandidateName(e.target.value)} style={{ display: 'block', margin: '10px auto', padding: '10px', width: '80%', maxWidth: '400px' }} />
                    <input type="text" placeholder="사진 URL" value={candidatePhotoUrl} onChange={(e) => setCandidatePhotoUrl(e.target.value)} style={{ display: 'block', margin: '10px auto', padding: '10px', width: '80%', maxWidth: '400px' }} />
                    <button onClick={handleAddCandidate} disabled={isLoading} style={{ padding: '10px 20px' }}>{isLoading ? '처리 중...' : '후보 등록'}</button>
                </div>
            )}

            {activeTab === 'whitelist' && (
                <div>
                    <h3>화이트리스트 추가</h3>
                    <textarea placeholder="지갑 주소를 쉼표(,)로 구분하여 입력하세요" value={whitelistAddresses} onChange={(e) => setWhitelistAddresses(e.target.value)} style={{ display: 'block', margin: '10px auto', padding: '10px', width: '80%', maxWidth: '400px', height: '100px' }}></textarea>
                    <button onClick={handleAddWhitelist} disabled={isLoading} style={{ padding: '10px 20px' }}>{isLoading ? '처리 중...' : '화이트리스트 추가'}</button>
                </div>
            )}

            {activeTab === 'control' && (
                <div>
                    <h3>투표 제어</h3>
                    {startTime === 0 ? (
                        <div style={{ backgroundColor: '#fff3e0', padding: '20px', borderRadius: '8px', border: '1px solid #ffe0b2' }}>
                            <p><strong>투표 시작 전입니다.</strong> 후보자와 유권자 등록을 완료한 후 투표를 시작해 주세요.</p>
                            <div style={{ marginTop: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '10px' }}>
                                    투표 유지 기간 (일): 
                                    <input 
                                        type="number" 
                                        min="1" 
                                        max="14" 
                                        value={duration} 
                                        onChange={(e) => setDuration(Number(e.target.value))}
                                        style={{ marginLeft: '10px', padding: '5px', width: '50px' }}
                                    />
                                </label>
                                <button 
                                    onClick={handleStartVoting} 
                                    disabled={isLoading}
                                    style={{ padding: '10px 30px', backgroundColor: '#e65100', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                                >
                                    {isLoading ? '트랜잭션 진행 중...' : '투표 시작 (Start Voting)'}
                                </button>
                                <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>* 투표 시작 후에는 후보자를 더 이상 추가할 수 없습니다. (최대 14일)</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <p>현재 상태: <strong>{isPaused ? '일시 정지됨' : '진행 중 (또는 종료됨)'}</strong></p>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                                {!isPaused ? (
                                    <button onClick={() => handlePause(true)} disabled={isLoading} style={{ padding: '10px', backgroundColor: 'orange', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>투표 일시 정지</button>
                                ) : (
                                    <button onClick={() => handlePause(false)} disabled={isLoading} style={{ padding: '10px', backgroundColor: 'green', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>투표 재개</button>
                                )}
                                <button onClick={handleForceEnd} disabled={isLoading} style={{ padding: '10px', backgroundColor: 'red', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>투표 즉시 종료</button>
                            </div>
                        </>
                    )}
                    
                    <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px dashed #ccc' }}>
                        <h4 style={{ color: 'red' }}>위험 구역: 권한 양도</h4>
                        <input type="text" placeholder="새 관리자 지갑 주소" value={newOwnerAddress} onChange={(e) => setNewOwnerAddress(e.target.value)} style={{ padding: '10px', width: '60%', maxWidth: '300px' }} />
                        <button onClick={handleTransferOwnership} disabled={isLoading} style={{ marginLeft: '10px', padding: '10px', backgroundColor: 'black', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>권한 양도 실행</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;